import React from 'react';

import {
	LoadingContainer,
	Wrapper,
	Header,
	InputWrapper,
	SearchWrapper,
	IconWrapper,
	TooltipContent,
	Label,
	DpoWrapper,
	RangeWrapper,
	Divider,
	RightSection,
	NoData,
} from './PTItemsStep.styles';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApInput,
	ApIcon,
	colors,
	ApToggleSwitch,
} from '@alixpartners/ui-components';
import { DpoFilter } from '../DpoFilter';
import { CategoryTreeFilter } from '../CategoryTreeFilter';
import { Tooltip } from 'react-tippy';
import { PTItemsTable } from '../PTItemsTable';
import { SearchInput } from 'components/SearchInput';
import Slider from 'rc-slider';
import { ITEMS_TYPE } from '../../utils';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';

export class PTItemsStep extends React.Component {
	state = {
		searchItem: null,
		dpoRange: [0, 0],
		minMax: [0, 0],
		dpoFilter: [],
	};

	componentDidMount() {
		const { initiativeData } = this.props;
		const { initiativeItemType } = initiativeData;

		if (initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT) {
			this.updateDefaultDpoRange();
		}
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.clearSearchItem);
	}

	componentDidUpdate(prevProps, prevState) {
		const { searchItem } = this.state;
		const { data, filterPTCategory } = this.props;

		if (data && data !== prevProps.data) {
			this.updateDefaultDpoRange();
		}

		if (searchItem !== prevState.searchItem) {
			if (searchItem) {
				document.addEventListener('click', this.clearSearchItem);
			} else {
				document.removeEventListener('click', this.clearSearchItem);
			}
		}

		if (!isEqual(filterPTCategory, prevProps.filterPTCategory)) {
			this.setState({ dpoFilter: [] });
		}
	}

	updateDefaultDpoRange() {
		const { data } = this.props;

		if (!data) {
			return;
		}

		const maxValue = Math.max.apply(
			null,
			data.map(item => item.weightedAverageDpo)
		);

		this.setState({
			dpoRange: [0, maxValue],
			minMax: [0, maxValue],
		});
	}

	handleRangeLowerChange = value => {
		const { dpoRange } = this.state;

		let number = Number(value);

		if (number > dpoRange[1]) {
			number = dpoRange[1];
		}

		if (number < 0) {
			number = 0;
		}

		this.setState({ dpoRange: [number, dpoRange[1]] });
	};

	handleRangeUpperChange = value => {
		const { dpoRange, minMax } = this.state;
		let number = Number(value);

		if (number > minMax[1]) {
			number = minMax[1];
		}

		if (number < 0) {
			number = 0;
		}

		this.setState({ dpoRange: [dpoRange[0], number] });
	};

	handleDpoRangeChange = dpoRange => {
		this.setState({ dpoRange });
	};

	getFilteredData = () => {
		const { data, initiativeData } = this.props;
		const { dpoRange, dpoFilter } = this.state;
		const { initiativeItemType } = initiativeData;

		if (initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT) {
			return data.filter(
				item =>
					item.weightedAverageDpo > dpoRange[0] &&
					item.weightedAverageDpo <= dpoRange[1]
			);
		}

		return dpoFilter.length > 0
			? data.filter(item => dpoFilter.includes(item.harmonizedDpo))
			: data;
	};

	getDpoOptions = () => {
		const { data } = this.props;

		return sortBy(
			data.reduce((acc, item) => {
				const currentIndex = acc.findIndex(
					dpo => dpo.value === item.harmonizedDpo
				);

				if (currentIndex === -1) {
					return [
						...acc,
						{
							value: item.harmonizedDpo,
							label: item.harmonizedDpo,
							vendorsCount: 1,
						},
					];
				}

				return acc.map((dpoOption, index) => {
					if (currentIndex === index) {
						return {
							...dpoOption,
							vendorsCount: dpoOption.vendorsCount + 1,
						};
					}

					return dpoOption;
				});
			}, []),
			'value'
		);
	};

	onDpoFilterChange = dpoFilter => {
		this.setState({
			dpoFilter,
		});
	};

	selectSearchItem = searchItem => {
		this.setState({
			searchItem,
		});
	};

	clearSearchItem = () => {
		this.setState({
			searchItem: null,
		});
	};

	getSearchOptions = () => {
		const { data, spendFormatter } = this.props;

		if (!data) {
			return [];
		}

		return data.map(({ name, id, groupName, spend }) => ({
			name,
			groupName,
			value: name,
			id,
			info: spendFormatter(spend),
		}));
	};

	handleItemsChange = items => {
		const { onInitiativeChange } = this.props;

		onInitiativeChange({ items });
	};

	render() {
		const {
			data,
			companies,
			spendFormatter,
			initiativeData,
			taxonomies,
			filterPTCategory,
			onFilterCategoryChange,
			isLoading,
			isCommonVendors,
			onCommonVendorsChange,
			onWaccChange,
			showTopVendors,
		} = this.props;
		const { initiativeItemType, items, wacc = 0 } = initiativeData;
		const { searchItem, dpoRange, minMax, dpoFilter } = this.state;

		if (!data || !taxonomies) {
			return (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			);
		}

		const filteredData = this.getFilteredData();

		return (
			<Wrapper>
				<Header>
					<Label>WACC:</Label>
					<InputWrapper>
						<ApInput
							symbol="%"
							value={String(wacc)}
							onChange={onWaccChange}
							type="number"
						/>
					</InputWrapper>
					<IconWrapper>
						<Tooltip
							position="top"
							animation="fade"
							arrow
							html={
								<TooltipContent>
									Weighted Average Cost of Capital
								</TooltipContent>
							}
							trigger="mouseenter"
						>
							<ApIcon
								iconName="baseline_help"
								iconSize={16}
								iconColor={colors.grey6}
							/>
						</Tooltip>
					</IconWrapper>
					{initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT ? (
						<React.Fragment>
							<Divider />
							<Label>Wt. Avg. DPO</Label>
							<DpoWrapper>
								<InputWrapper>
									<ApInput
										value={String(dpoRange[0])}
										onChange={this.handleRangeLowerChange}
										type="number"
									/>
								</InputWrapper>
								<RangeWrapper>
									<Slider
										range
										value={dpoRange}
										min={minMax[0]}
										max={minMax[1]}
										allowCross={false}
										onChange={this.handleDpoRangeChange}
									/>
								</RangeWrapper>
								<InputWrapper>
									<ApInput
										value={String(dpoRange[1])}
										onChange={this.handleRangeUpperChange}
										type="number"
									/>
								</InputWrapper>
							</DpoWrapper>
						</React.Fragment>
					) : (
						<RightSection>
							<ApToggleSwitch
								checked={isCommonVendors}
								onChange={onCommonVendorsChange}
							>
								Common vendors
							</ApToggleSwitch>
							<Divider />

							<CategoryTreeFilter
								data={taxonomies}
								onChange={onFilterCategoryChange}
								selectedItem={filterPTCategory}
							/>
							<DpoFilter
								options={this.getDpoOptions()}
								selected={dpoFilter}
								onChange={this.onDpoFilterChange}
							/>
						</RightSection>
					)}
				</Header>

				{isLoading ? (
					<LoadingContainer>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoadingContainer>
				) : filteredData && filteredData.length > 0 ? (
					<PTItemsTable
						data={filteredData}
						itemsType={initiativeItemType}
						companies={companies}
						wacc={wacc}
						items={items}
						spendFormatter={spendFormatter}
						onItemsChange={this.handleItemsChange}
						highlightId={searchItem ? searchItem.id : null}
						showTopVendors={showTopVendors}
					/>
				) : (
					<NoData>No data available</NoData>
				)}

				<SearchWrapper>
					<SearchInput
						options={this.getSearchOptions()}
						onChange={this.selectSearchItem}
						placeholder="Search"
						placement="top-start"
						charCount={2}
						showIcon
						isDismissible
					/>
				</SearchWrapper>
			</Wrapper>
		);
	}
}

export default PTItemsStep;
